import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout';
import ApiClient from '../../utils/ApiClient';
import envConfig from '../../utils/EnvironmentConfigs';
import { formatRequestId } from '../../utils/RequestIdHelper';
import { firstResolvedDisplayName } from '../../utils/DisplayValueUtils';
import { formatStatusForDisplay } from '../../utils/StatusDisplayHelper';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';

export const normalizeIdentifier = (value) => {
    if (!value) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value).trim();
    }
    if (typeof value === 'object') {
        return String(value.id ?? value.uuid ?? '').trim();
    }
    return '';
};

export const normalizeDisplayText = (value) => {
    if (!value) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value).trim();
    }
    if (typeof value === 'object') {
        return String(value.name ?? value.displayName ?? '').trim();
    }
    return '';
};

export const normalizeObjectDisplayText = (value) => {
    if (!value || typeof value !== 'object') {
        return '';
    }
    return String(value.name ?? value.displayName ?? '').trim();
};

export const extractRequestList = (responseData) =>
    Array.isArray(responseData) ? responseData : responseData?.content ?? responseData?.requests ?? [];

export const extractSupervisorId = (request) =>
    normalizeIdentifier(
        request.supervisorUuid ??
            request.supervisorId ??
            request.supervisorUserId ??
            request.supervisorUserUuid ??
            request.securityManagerUuid ??
            request.securityManagerId ??
            request.supervisor
    );

export const extractEntitlementId = (request) =>
    normalizeIdentifier(
        request.groupUuid ??
            request.groupId ??
            request.group
    );

export const extractEntitlementOwnerGroupId = (request) =>
    normalizeIdentifier(
        request.entitlementOwnerUuid ??
            request.entitlementOwnerId ??
            request.entitlementOwner ??
            request.entitlementOwnerGroupUuid ??
            request.entitlementOwnerGroupId ??
            (Array.isArray(request.entitlementOwners) ? request.entitlementOwners[0] : null) ??
            request.ownerUuid ??
            request.ownerId ??
            request.owner
    );

export const resolveUserName = (user, fallbackLabel = 'Unknown user') =>
    firstResolvedDisplayName(
        [
            user?.name,
            user?.displayName,
            `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            user?.preferred_username,
            user?.username,
            user?.email,
        ],
        fallbackLabel
    );

export const resolveGroupName = (group, fallbackLabel = 'Unknown group') =>
    firstResolvedDisplayName([group?.name, group?.displayName], fallbackLabel);

export const normalizeRoleName = (role) => {
    if (!role) {
        return '';
    }

    if (typeof role === 'string' || typeof role === 'number') {
        return String(role).trim();
    }

    if (typeof role === 'object') {
        return String(role.name ?? role.displayName ?? role.id ?? role.uuid ?? '').trim();
    }

    return '';
};

export const extractRoleNames = (request) => {
    const roles = Array.isArray(request?.roles) ? request.roles : [];
    const normalizedRoles = roles
        .map((role) => normalizeRoleName(role))
        .filter(Boolean);

    if (normalizedRoles.length > 0) {
        return normalizedRoles;
    }

    const singleRole = normalizeRoleName(request?.role ?? request?.roleName);
    return singleRole ? [singleRole] : [];
};

export const REQUEST_FILTER_OPTIONS = {
    OPEN: 'open',
    CLOSED: 'closed',
    ALL: 'all',
};

export const CLOSED_REQUEST_STATUSES = new Set([
    'APPROVED',
    'CLOSED',
    'COMPLETED',
    'CANCELLED',
    'DENIED',
    'FULFILLED',
    'IMPLEMENTED',
    'REJECTED',
    'RESOLVED',
    'WITHDRAWN',
]);

export const isClosedRequest = (request) => {
    if (typeof request?.closed === 'boolean') {
        return request.closed;
    }

    const status = String(request?.state ?? request?.status ?? '')
        .trim()
        .toUpperCase();
    if (!status) {
        return false;
    }

    if (CLOSED_REQUEST_STATUSES.has(status)) {
        return true;
    }

    return status.includes('CLOSED') || status.includes('COMPLETE');
};

const RequestsPage = ({ authDetails, onSignOut }) => {
    const [requests, setRequests] = useState([]);
    const [requestsError, setRequestsError] = useState('');
    const [isLoadingRequests, setIsLoadingRequests] = useState(true);
    const [isLoadingLookups, setIsLoadingLookups] = useState(false);
    const [groupLookup, setGroupLookup] = useState(new Map());
    const [userLookup, setUserLookup] = useState(new Map());
    const [requestFilter, setRequestFilter] = useState(REQUEST_FILTER_OPTIONS.OPEN);

    useEffect(() => {
        let isMounted = true;
        const apiUrl = envConfig.apiBaseUrl;
        const requestorUuid =
            authDetails?.identity?.tokenParsed?.sub ?? authDetails?.identity?.subject ?? null;

        if (!apiUrl) {
            setRequestsError('API URL is not configured.');
            setIsLoadingRequests(false);
            return () => {
                isMounted = false;
            };
        }

        if (!requestorUuid) {
            setRequestsError('Your session is missing a user identifier.');
            setIsLoadingRequests(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchRequests = async () => {
            try {
                const response = await ApiClient.get('/access-requests', {
                    params: {
                        page: 0,
                        size: 100,
                        requester: requestorUuid,
                    },
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    const responseData = response.data ?? {};
                    const requestList = extractRequestList(responseData);
                    const normalizedRequestList = Array.isArray(requestList) ? requestList : [];
                    setIsLoadingLookups(normalizedRequestList.length > 0);
                    setRequests(normalizedRequestList);
                }
            } catch (error) {
                if (isMounted) {
                    setRequestsError('We could not load your access requests.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingRequests(false);
                }
            }
        };

        fetchRequests();

        return () => {
            isMounted = false;
        };
    }, [authDetails]);

    useEffect(() => {
        let isMounted = true;
        const groupIds = new Set();
        const userIds = new Set();

        requests.forEach((request) => {
            const entitlementId = extractEntitlementId(request);
            if (entitlementId) {
                groupIds.add(entitlementId);
            }

            const ownerGroupId = extractEntitlementOwnerGroupId(request);
            if (ownerGroupId) {
                groupIds.add(ownerGroupId);
            }

            const supervisorId = extractSupervisorId(request);
            if (supervisorId) {
                userIds.add(supervisorId);
            }
        });

        if (groupIds.size === 0) {
            setGroupLookup(new Map());
        }

        if (userIds.size === 0) {
            setUserLookup(new Map());
        }

        if (groupIds.size === 0 && userIds.size === 0) {
            setIsLoadingLookups(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchLookups = async () => {
            setIsLoadingLookups(true);
            try {
                const [groups, users] = await Promise.all([
                    Promise.all(
                        Array.from(groupIds).map((groupId) =>
                            ApiClient.get(`/groups/${encodeURIComponent(groupId)}`)
                                .then((response) => ({
                                    id: groupId,
                                    name: resolveGroupName(response?.data, groupId),
                                }))
                                .catch(() => null)
                        )
                    ),
                    Promise.all(
                        Array.from(userIds).map((userId) =>
                            ApiClient.get(`/users/${encodeURIComponent(userId)}`)
                                .then((response) => ({
                                    id: userId,
                                    name: resolveUserName(response?.data, userId),
                                }))
                                .catch(() => null)
                        )
                    ),
                ]);

                if (!isMounted) {
                    return;
                }

                const nextGroupLookup = new Map();
                groups.filter(Boolean).forEach((group) => {
                    if (group.id && group.name) {
                        nextGroupLookup.set(group.id, group.name);
                    }
                });
                setGroupLookup(nextGroupLookup);

                const nextUserLookup = new Map();
                users.filter(Boolean).forEach((user) => {
                    if (user.id && user.name) {
                        nextUserLookup.set(user.id, user.name);
                    }
                });
                setUserLookup(nextUserLookup);
            } catch (error) {
                if (isMounted) {
                    setGroupLookup(new Map());
                    setUserLookup(new Map());
                }
            } finally {
                if (isMounted) {
                    setIsLoadingLookups(false);
                }
            }
        };

        fetchLookups();

        return () => {
            isMounted = false;
        };
    }, [requests]);

    const getRequestId = (request) =>
        request.id ?? request.requestId ?? request.uuid ?? request.accessRequestId ?? 'Unknown';
    const getDisplayRequestId = (request) => formatRequestId(getRequestId(request));
    const getEntitlementName = (request) => {
        const explicitName =
            normalizeDisplayText(request.entitlement) ||
            normalizeDisplayText(request.entitlementName) ||
            normalizeDisplayText(request.groupName) ||
            normalizeDisplayText(request.roleName);
        if (explicitName) {
            return explicitName;
        }

        const entitlementId = extractEntitlementId(request);
        if (entitlementId) {
            return groupLookup.get(entitlementId) ?? 'Unknown entitlement';
        }

        const roleNames = extractRoleNames(request);
        if (roleNames.length > 0) {
            return roleNames.join(', ');
        }

        return 'Unknown entitlement';
    };
    const getEntitlementType = (request) =>
        request.type ??
        request.entitlementType ??
        (request.group || request.groupName || request.groupUuid || request.groupId
            ? 'Group'
            : request.role || request.roleName || request.roles
              ? 'Role'
              : 'Request');
    const getSupervisorName = (request) => {
        const explicitName =
            normalizeDisplayText(request.supervisorName) ||
            normalizeDisplayText(request.securityManagerName) ||
            normalizeObjectDisplayText(request.supervisor);
        if (explicitName) {
            return explicitName;
        }

        const supervisorId = extractSupervisorId(request);
        if (supervisorId) {
            return userLookup.get(supervisorId) ?? 'Unknown user';
        }

        return 'Unassigned';
    };
    const getOwnerName = (request) => {
        const explicitName =
            normalizeDisplayText(request.entitlementOwnerName) ||
            normalizeObjectDisplayText(request.entitlementOwner) ||
            normalizeObjectDisplayText(request.owner);
        if (explicitName) {
            return explicitName;
        }

        const ownerGroupId = extractEntitlementOwnerGroupId(request);
        if (ownerGroupId) {
            return groupLookup.get(ownerGroupId) ?? 'Unknown group';
        }

        return 'Unassigned';
    };
    const getStatusLabel = (request) =>
        formatStatusForDisplay(request.state ?? request.status, 'Pending');
    const requestCounts = useMemo(() => {
        const closed = requests.filter((request) => isClosedRequest(request)).length;
        const open = requests.length - closed;
        return {
            open,
            closed,
            all: requests.length,
        };
    }, [requests]);
    const filteredRequests = useMemo(() => {
        if (requestFilter === REQUEST_FILTER_OPTIONS.OPEN) {
            return requests.filter((request) => !isClosedRequest(request));
        }
        if (requestFilter === REQUEST_FILTER_OPTIONS.CLOSED) {
            return requests.filter((request) => isClosedRequest(request));
        }
        return requests;
    }, [requestFilter, requests]);
    const emptyFilterMessage =
        requestFilter === REQUEST_FILTER_OPTIONS.OPEN
            ? 'No open access requests found.'
            : requestFilter === REQUEST_FILTER_OPTIONS.CLOSED
              ? 'No closed access requests found.'
              : 'No access requests have been submitted yet.';

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-6">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Make a new access request</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        <p className="usa-prose">
                                            Start a new request for a JCWA system entitlement.
                                        </p>
                                    </div>
                                    <div className="usa-card__footer">
                                        <Link className="usa-button" to="/createRequest">
                                            Create request
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tablet:grid-col-6">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">My access requests</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {isLoadingRequests ? (
                                            <div className="usa-form" aria-hidden="true">
                                                <fieldset className="usa-fieldset">
                                                    {Array.from({ length: 3 }).map((_, index) => (
                                                        <div key={`request-loading-${index}`} className="usa-checkbox">
                                                            <input
                                                                className="usa-checkbox__input usa-checkbox__input--tile"
                                                                type="checkbox"
                                                                disabled
                                                            />
                                                            <label className="usa-checkbox__label">
                                                                <ValueWithSkeleton loading width="9ch" />
                                                                <span className="usa-checkbox__label-description">
                                                                    <ValueWithSkeleton loading type="sentence" width="22ch" />
                                                                </span>
                                                                <span className="usa-checkbox__label-description">
                                                                    <ValueWithSkeleton loading type="sentence" width="28ch" />
                                                                </span>
                                                            </label>
                                                        </div>
                                                    ))}
                                                </fieldset>
                                            </div>
                                        ) : requestsError ? (
                                            <div className="usa-alert usa-alert--error" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{requestsError}</p>
                                                </div>
                                            </div>
                                        ) : requests.length === 0 ? (
                                            <p>No access requests have been submitted yet.</p>
                                        ) : (
                                            <div>
                                                <div className="margin-bottom-2">
                                                    <fieldset className="usa-segmented-switch" aria-label="Filter request list">
                                                        <input
                                                            className="usa-segmented-switch__input"
                                                            type="radio"
                                                            id="request-filter-open"
                                                            name="request-filter"
                                                            checked={requestFilter === REQUEST_FILTER_OPTIONS.OPEN}
                                                            onChange={() => setRequestFilter(REQUEST_FILTER_OPTIONS.OPEN)}
                                                        />
                                                        <label className="usa-segmented-switch__label" htmlFor="request-filter-open">
                                                            Open ({requestCounts.open})
                                                        </label>

                                                        <input
                                                            className="usa-segmented-switch__input"
                                                            type="radio"
                                                            id="request-filter-closed"
                                                            name="request-filter"
                                                            checked={requestFilter === REQUEST_FILTER_OPTIONS.CLOSED}
                                                            onChange={() => setRequestFilter(REQUEST_FILTER_OPTIONS.CLOSED)}
                                                        />
                                                        <label className="usa-segmented-switch__label" htmlFor="request-filter-closed">
                                                            Closed ({requestCounts.closed})
                                                        </label>

                                                        <input
                                                            className="usa-segmented-switch__input"
                                                            type="radio"
                                                            id="request-filter-all"
                                                            name="request-filter"
                                                            checked={requestFilter === REQUEST_FILTER_OPTIONS.ALL}
                                                            onChange={() => setRequestFilter(REQUEST_FILTER_OPTIONS.ALL)}
                                                        />
                                                        <label className="usa-segmented-switch__label" htmlFor="request-filter-all">
                                                            All ({requestCounts.all})
                                                        </label>
                                                    </fieldset>
                                                </div>
                                                {filteredRequests.length === 0 ? (
                                                    <p>{emptyFilterMessage}</p>
                                                ) : (
                                                    <div className="usa-form">
                                                        <fieldset className="usa-fieldset">
                                                            {filteredRequests.map((request, index) => {
                                                                const requestId = getRequestId(request);
                                                                const displayRequestId = getDisplayRequestId(request);
                                                                const inputId =
                                                                    requestId && requestId !== 'Unknown'
                                                                        ? requestId
                                                                        : `request-${index + 1}`;
                                                                const hasDisplayRequestId = displayRequestId && displayRequestId !== 'Unknown';
                                                                return (
                                                                    <div key={inputId} className="usa-checkbox">
                                                                        <input
                                                                            className="usa-checkbox__input usa-checkbox__input--tile"
                                                                            id={`request-${inputId}`}
                                                                            type="checkbox"
                                                                            name="access-requests"
                                                                            disabled
                                                                        />
                                                                        <label
                                                                            className="usa-checkbox__label"
                                                                            htmlFor={`request-${inputId}`}
                                                                        >
                                                                            {hasDisplayRequestId ? (
                                                                                <Link to={`/requests/${displayRequestId}`}>
                                                                                    {displayRequestId}
                                                                                </Link>
                                                                            ) : (
                                                                                <ValueWithSkeleton value="" fallback="Unknown" />
                                                                            )}
                                                                            <span className="usa-checkbox__label-description">
                                                                                <ValueWithSkeleton
                                                                                    value={getEntitlementName(request)}
                                                                                    loading={isLoadingLookups}
                                                                                    type="sentence"
                                                                                    fallback="Unknown entitlement"
                                                                                />{' '}
                                                                                ·{' '}
                                                                                {getEntitlementType(request)}
                                                                            </span>
                                                                            <span className="usa-checkbox__label-description">
                                                                                Supervisor:{' '}
                                                                                <ValueWithSkeleton
                                                                                    value={getSupervisorName(request)}
                                                                                    loading={isLoadingLookups}
                                                                                    type="text"
                                                                                    width="12ch"
                                                                                    fallback="Unknown user"
                                                                                />{' '}
                                                                                · Entitlement owner:{' '}
                                                                                <ValueWithSkeleton
                                                                                    value={getOwnerName(request)}
                                                                                    loading={isLoadingLookups}
                                                                                    type="text"
                                                                                    width="12ch"
                                                                                    fallback="Unknown group"
                                                                                />
                                                                            </span>
                                                                            <span className="usa-checkbox__label-description">
                                                                                Status: {getStatusLabel(request)}
                                                                            </span>
                                                                        </label>
                                                                    </div>
                                                                );
                                                            })}
                                                        </fieldset>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default RequestsPage;
